import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { apiCallBegan } from '../store/api'
import { FaAsterisk } from '@react-icons/all-files/fa/FaAsterisk'
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding'
import { FaRegBuilding } from '@react-icons/all-files/fa/FaRegBuilding'
import Button from './Button'
import CanonicalBusinessLocations from './location/CanonicalBusinessLocations'
import { showAlert } from '../store/slices/ui'
import { useRouter } from 'next/router'

const uniqueIds = locations => [...new Set(locations.map(location => String(location._id)))]

const SignIntro = ({ potential, setStep }) => {
    const dispatch = useDispatch()
    const { handleSubmit, register, setValue, errors } = useForm()
    const [selected, setSelected] = useState(potential?.business_type || '')
    const [locations, setLocations] = useState(potential?.locations?.length ? potential.locations : [null])
    const [saving, setSaving] = useState(false)
    const { push } = useRouter()

    useEffect(() => {
        if (!potential?._id) return
        setValue('name', potential.name)
        setValue('company_name', potential.company_name)
        setValue('sub_type', potential.sub_type)
        setSelected(potential.business_type || '')
        setLocations(potential.locations?.length ? potential.locations : [null])
    }, [potential, setValue])

    const submit = async formData => {
        const businessType = potential?.business_type || selected
        if (!businessType) {
            return dispatch(showAlert({ type: 'error', alert: 'Please select the business type that best describes your business' }))
        }

        const selectedLocations = locations.filter(Boolean)
        if (businessType === 'venue' && !selectedLocations.length) {
            return dispatch(showAlert({ type: 'error', alert: 'Please select at least one verified venue location.' }))
        }

        setSaving(true)
        const business = await dispatch(apiCallBegan({
            url: potential?._id ? `/business/edits/${potential._id}` : '/business',
            method: potential?._id ? 'PUT' : 'POST',
            data: { ...formData, business_type: businessType },
            showSpinner: true,
            returnErrorData: true,
        }))

        if (!business?._id || business?.errorMessage || business?.httpStatus) {
            setSaving(false)
            return
        }

        if (businessType === 'venue') {
            const resolvedLocations = await Promise.all(selectedLocations.map(location => dispatch(apiCallBegan({
                url: '/location/create',
                method: 'POST',
                data: location?._id ? { _id: location._id } : location,
                returnErrorData: true,
            }))))

            if (resolvedLocations.some(location => !location?._id || location?.errorMessage || location?.httpStatus)) {
                setSaving(false)
                return dispatch(showAlert({ type: 'error', alert: 'The business was saved, but one or more venue locations could not be linked. Please review the Locations step and try again.' }))
            }

            const businessWithLocations = await dispatch(apiCallBegan({
                url: `/location/businesses/${business._id}/locations`,
                method: 'PUT',
                data: { location_ids: uniqueIds(resolvedLocations) },
                showSpinner: true,
                returnErrorData: true,
            }))
            if (!businessWithLocations?._id || businessWithLocations?.errorMessage || businessWithLocations?.httpStatus) {
                setSaving(false)
                return
            }
        }

        if (!potential?._id) await push(`/partnerships?id=${business._id}`)
        setSaving(false)
        setStep(1)
    }

    return <form className='signCont canonical-partnership-intro' onSubmit={handleSubmit(submit)}>
        <div className='row'>
            <div className='inputIcon'>
                <input id='name' name='name' placeholder='Business Name' ref={register({ required: 'Please input your business name' })} />
                <label id='name-label' htmlFor='name'><FaRegBuilding /></label>
                <FaAsterisk className='mandatory' />
                {errors.name && <strong className='colorText red'>{errors.name?.message}</strong>}
            </div>

            <div className='inputIcon'>
                <input id='company_name' name='company_name' placeholder='Company Name (if different)' ref={register} />
                <label id='company-name-label' htmlFor='company_name'><FaBuilding /></label>
            </div>
        </div>

        {!potential?._id && <div className='busType'>
            <strong>Business Type</strong>
            <div className='row'>
                <Button type='button' className={`btn-3 ${selected === 'venue' ? 'highLight' : ''}`} onClick={() => setSelected('venue')}>Event Venue</Button>
                <Button type='button' className={`btn-3 ${selected === 'services' ? 'highLight' : ''}`} onClick={() => setSelected('services')}>Event Services Provider</Button>
                <Button type='button' className={`btn-3 ${selected === 'finder' ? 'highLight' : ''}`} onClick={() => setSelected('finder')}>Venue Finder</Button>
            </div>
        </div>}

        {['venue', 'services'].includes(potential?.business_type || selected) && <div className='row'>
            <div className='inputIcon state'>
                <select id='sub_type' name='sub_type' ref={register}>
                    {(potential?.business_type || selected) === 'venue' ? <>
                        <option value=''>Venue Type</option>
                        <option value='hotel'>Hotel</option>
                        <option value='leisure'>Restaurant / Bar</option>
                        <option value='hall'>Banquet Hall</option>
                        <option value='flex'>Flex Space</option>
                    </> : <>
                        <option value=''>Type of Services Provided</option>
                        <option value='planner'>Event Planner</option>
                        <option value='agency'>Marketing Agency</option>
                        <option value='charter'>Charter Agency</option>
                        <option value='dmc'>Destination Management Company</option>
                    </>}
                </select>
                <label id='sub-type-label' htmlFor='sub_type'></label>
            </div>
        </div>}

        {(potential?.business_type === 'venue' || selected === 'venue') && <CanonicalBusinessLocations
            locations={locations}
            market={potential?.orlando ? 'orlando' : 'miami'}
            disabled={saving}
            onChange={setLocations}
        />}

        <div className='buttons'>
            <Button disabled={saving} className='btn-4 highLight'>{saving ? 'Saving…' : 'Continue'}</Button>
        </div>
    </form>
}

export default SignIntro
